import {
    Building,
    DysonSphere,
    Factory,
    FactoryGameType,
    IFacility,
    ItemStack,
    Satisfactory,
    VirtualNormalizedRecipe
} from "../types";
import {Building as BuildingDysonSphere, ProliferatorType, Item as ItemDysonSphere} from "../DSP/types";
import {OptionalId} from "../../index";

function groupItemStacks(itemStacks: ItemStack[]): ItemStack[] {
    return Object.values(
        itemStacks.reduce((returnValue, itemStack) => {
            if (returnValue[itemStack.item] === undefined) {
                returnValue[itemStack.item] = {
                    amount: 0,
                    item: itemStack.item,
                };
            }
            returnValue[itemStack.item].amount += itemStack.amount;
            return returnValue;
        }, {} as Record<string, ItemStack>)
    );
}

function getDysonSphereBuildingFactor(building: Building<DysonSphere>, machineTier: number): number {
    switch (building) {
        case BuildingDysonSphere.Smelter:
            return [null, 1, 2, 3][machineTier] ?? 3;
        case BuildingDysonSphere.Assembler:
            return [null, 0.75, 1, 1.5, 3][machineTier] ?? 3;
        case BuildingDysonSphere.Chemical:
            return [null, 1, 2][machineTier] ?? 2;
    }
    return 1;
}

export function aggregateFacility(facility: OptionalId<IFacility>): VirtualNormalizedRecipe {
    const recipe = _aggregateFacility(facility);

    // Group item stacks
    recipe.ingredients = groupItemStacks(recipe.ingredients);
    recipe.products = groupItemStacks(recipe.products);

    // Cancel items on both sides
    recipe.ingredients.forEach(stack => {
        const outputStack = recipe.products.find(findStack => findStack.item === stack.item);
        if (outputStack !== undefined) {
            const minValue = Math.min(stack.amount, outputStack.amount);
            stack.amount -= minValue;
            outputStack.amount -= minValue;
        }
    });
    // Filter out stacks, that are 0 now ... also hurray floating points
    recipe.ingredients = recipe.ingredients.filter(stack => stack.amount >= 0.01);
    recipe.products = recipe.products.filter(stack => stack.amount >= 0.01);

    return recipe;
}

function _aggregateFacility(facility: OptionalId<IFacility>): VirtualNormalizedRecipe {
    const input = [] as ItemStack[];
    const output = [] as ItemStack[];
    const modifierInputs = [] as ItemStack[];

    let recipe: VirtualNormalizedRecipe;
    let factor: number;
    let outputFactor: number = 1;
    switch (facility.type) {
        case 'Factory':
            const factory = facility as Factory;
            recipe = factory.recipe!;
            factor = factory.amount * (60 / factory.recipe.duration);
            switch (facility.game) {
                case FactoryGameType.DysonSphere:
                    const dysonFactory = factory as Factory<DysonSphere>;
                    factor *= getDysonSphereBuildingFactor(factory.recipe.building as BuildingDysonSphere, dysonFactory.machineTier);
                    switch (dysonFactory.proliferatorType) {
                        case ProliferatorType.Product:
                            outputFactor *= [1, 1.125, 1.200, 1.2500][dysonFactory.proliferatorTier];
                            break;
                        case ProliferatorType.Speed:
                            factor *= [1, 1.250, 1.500, 2.000][dysonFactory.proliferatorTier];
                            break;
                    }
                    if (dysonFactory.proliferatorTier !== 0 && dysonFactory.proliferatorType !== null) {
                        const itemsPerProliferator = [0, 13, 29, 75][dysonFactory.proliferatorTier] - 1;
                        const requiredProliferators = {
                            item: [null, ItemDysonSphere.ProliferatorMkI, ItemDysonSphere.ProliferatorMkII, ItemDysonSphere.ProliferatorMkIII][dysonFactory.proliferatorTier],
                            amount: recipe.ingredients
                                .map(ingredient => ingredient.amount)
                                .reduce((a, b) => a + b, 0) * factor / itemsPerProliferator,
                        } as ItemStack<DysonSphere>;
                        modifierInputs.push(requiredProliferators);
                    }
                    break;
                case FactoryGameType.Satisfactory:
                    factor *= (factory as Factory<Satisfactory>).clockSpeed;
                    break;
            }
            break;
        case 'Facility':
            recipe = facility.facilities
                .map(subFacility => _aggregateFacility(subFacility))
                .reduce(
                    (aggregate: VirtualNormalizedRecipe, current: VirtualNormalizedRecipe) => ({
                        ingredients: [...aggregate.ingredients, ...current.ingredients],
                        products: [...aggregate.products, ...current.products],
                    } as VirtualNormalizedRecipe),
                    { ingredients: [], products: [] } as VirtualNormalizedRecipe
                );
            factor = 1;
            break;
    }

    input.push(...recipe.ingredients.map(ingredient => ({...ingredient, amount: ingredient.amount * factor})));
    output.push(...recipe.products.map(product => ({...product, amount: product.amount * factor * outputFactor})));
    input.push(...modifierInputs);

    return {
        ingredients: input,
        products: output,
    };
}

type countFactoriesProps = {
    facilityIsOne: boolean,
}
const countFactoriesDefaults: countFactoriesProps = {
    facilityIsOne: false,
};

export function countFactories(facility: IFacility, { facilityIsOne = false }: Partial<countFactoriesProps> = countFactoriesDefaults): number {
    switch (facility.type) {
        case 'Factory':
            return 1;
        case 'Facility':
            return facility.facilities
                .map(facility => countFactories(facility))
                .reduce((a, b) => a + b, facilityIsOne ? 1 : 0);
    }
}

export function getAllContainedFactories(facility: IFacility): Factory[] {
    if (facility.type === 'Factory') {
        return [facility];
    }
    return facility.facilities.map(f => getAllContainedFactories(f)).flat(1);
}
